import axios from 'axios';
import axiosRetry from 'axios-retry';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true // 允许发送 cookie
});

axiosRetry(api, { 
  retries: 3, 
  retryDelay: (retryCount) => retryCount * 1000,
  retryCondition: (error) => axiosRetry.isNetworkError(error) || error.response?.status >= 500
});

// 获取 CSRF token
let csrfToken = null;
const getCsrfToken = async () => {
  if (!csrfToken) {
    try {
      const res = await api.get('/csrf-token');
      csrfToken = res.data.data.csrfToken;
    } catch (error) {
      console.error('Failed to get CSRF token:', error);
    }
  }
  return csrfToken;
};

api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  
  // 非 GET 请求添加 CSRF token
  const method = (config.method || 'get').toUpperCase();
  if (!token && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = await getCsrfToken();
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  
  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use((response) => response, (error) => {
  if (error.response?.status === 401) { 
    localStorage.removeItem('token'); 
    window.location.href = '/login'; 
  }
  // 全局错误处理会在 App.js 中通过 SnackbarContext 处理
  return Promise.reject(error);
});

export default api;

const rawApiBase = process.env.REACT_APP_API_URL || '/api';
const resolvedApiOrigin = rawApiBase.startsWith('http')
  ? new URL(rawApiBase).origin
  : window.location.origin;

export function buildAssetUrl(assetPath) {
  if (!assetPath) return '';
  if (/^https?:\/\//.test(assetPath)) return assetPath;
  return `${resolvedApiOrigin}${assetPath.startsWith('/') ? assetPath : `/${assetPath}`}`;
}

// 导出 axios 的 CancelToken 和 isCancel
export const CancelToken = axios.CancelToken;
export const isCancel = axios.isCancel;

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data)
};

export const configsAPI = {
  list: (params) => api.get('/configs', { params }),
  summary: (params) => api.get('/configs/summary', { params }),
  get: (id) => api.get(`/configs/${id}`),
  history: (id) => api.get(`/configs/${id}/history`),
  diffVersion: (id, versionId) => api.get(`/configs/${id}/history/${versionId}/diff`),
  compatibility: (id) => api.get(`/configs/${id}/compatibility`),
  fieldIssues: (id) => api.get(`/configs/${id}/field-issues`),
  create: (data) => api.post('/configs', data),
  update: (id, data) => api.put(`/configs/${id}`, data),
  restoreVersion: (id, versionId) => api.post(`/configs/${id}/restore/${versionId}`),
  delete: (id) => api.delete(`/configs/${id}`),
  batchDelete: (ids) => api.post('/configs/batch-delete', { ids }),
  batchTags: (ids, tags) => api.post('/configs/batch-tags', { ids, tags }),
  batchArchive: (ids, archive = true) => api.post('/configs/batch-archive', { ids, archive }),
  clone: (id) => api.post(`/configs/clone/${id}`),
  toggleFavorite: (id) => api.post(`/configs/favorite/${id}`),
  trackView: (id) => api.post(`/configs/view/${id}`),
  upload: (formData, config) => api.post('/configs/upload', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    ...config
  }),
  standardizePreview: (fields, scope = {}) => api.get('/configs/standardize-preview', {
    params: {
      fields: Array.isArray(fields) ? fields.join(',') : fields,
      ...scope
    }
  }),
  standardize: (fields, scope = {}) => api.post('/configs/standardize', { fields, ...scope }),
  import: (data) => api.post('/configs/import', data),
  uploadImageStandalone: (formData) => api.post('/configs/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadImage: (id, formData) => api.post(`/configs/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  compare: (configIds, options = {}) => api.post('/configs/compare', { configIds, ...options }),
  batchExport: (ids) => api.post('/configs/batch-export', { ids }, { responseType: 'blob' }),
  export: (id, format) => api.get(`/configs/${id}/export?format=${format}`, { responseType: 'blob' }),
  CancelToken,
  isCancel
};

export const componentsAPI = {
  list: (params) => api.get('/components', { params }),
  get: (id) => api.get(`/components/${id}`),
  create: (data) => api.post('/components', data),
  update: (id, data) => api.put(`/components/${id}`, data),
  delete: (id) => api.delete(`/components/${id}`),
  bulkDelete: (ids) => api.post('/components/bulk-delete', { ids }),
  markNonComponent: (ids) => api.post('/components/mark-non-component', { ids }),
  duplicatesPreview: (scope = {}) => api.get('/components/duplicates-preview', { params: scope }),
  mergeDuplicates: (ids, targetId, fields) => api.post('/components/merge-duplicates', { ids, targetId, fields }),
  mergeHistory: () => api.get('/components/merge-history'),
  rollbackDuplicateMerge: (componentId, historyId) => api.post(`/components/merge-duplicates/${componentId}/rollback`, { historyId }),
  qualityDictionary: () => api.get('/components/quality-dictionary'),
  summary: () => api.get('/components/summary'),
  categories: () => api.get('/components/categories'),
  compatibility: (id, params) => api.get(`/components/${id}/compatibility`, { params }),
  generate: (data) => api.post('/components/generate', data),
  standardizePreview: (fields, scope = {}) => api.get('/components/standardize-preview', {
    params: {
      fields: Array.isArray(fields) ? fields.join(',') : fields,
      ...scope
    }
  }),
  syncFromConfigs: () => api.post('/components/sync-from-configs'),
  standardize: (fields, scope = {}) => api.post('/components/standardize', { fields, ...scope })
};

export const analysisAPI = {
  trends: () => api.get('/analysis/trends'),
  cost: (configId) => api.get(`/analysis/cost/${configId}`),
  competitors: (params) => api.get('/analysis/competitors', { params }),
  addCompetitor: (data) => api.post('/analysis/competitors', data),
  updateCompetitor: (id, data) => api.put(`/analysis/competitors/${id}`, data),
  deleteCompetitor: (id) => api.delete(`/analysis/competitors/${id}`),
  compare: (data) => api.post('/analysis/compare', data),
  aiCompetitor: (data) => api.post('/analysis/ai-competitor', data),
  aiTrends: (data) => api.post('/analysis/ai-trends', data),
  aiSuggestion: (data) => api.post('/analysis/ai-suggestion', data)
};

export const operationsAPI = {
  recent: () => api.get('/operations/recent')
};
